import axios from 'axios';
import toast from 'react-hot-toast';
import useAppStore from './microsite-store';

/**
 * Microsite user lookup utility
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.userInfo - User information (email, phone, etc.)
 * @param {Function} options.onModal - Modal callback for lock data
 * @param {Function} options.onRegistration - Registration callback
 * @returns {Promise} - Promise that resolves with user data
 */
export async function micrositeUserLookup({
  userInfo = {},
  onModal,
  onRegistration,
}) {
  // Get current store state and actions
  const {
    campaignId,
    entityInfo,
    location,
    coords,
    setUser,
    setEmail,
    setPhone,
    setEmailStreams,
    setPreferredLocation,
    setRegistrationInfo,
    user,
  } = useAppStore.getState();

  // Build request info
  const req = {};

  // Location handling
  const locationId = entityInfo?.location_id || location?.id;
  if (locationId) req.location_id = locationId;

  // Campaign/Effort info
  if (entityInfo.campaign_effort_id)
    req.campaign_effort_id = entityInfo.campaign_effort_id;
  if (campaignId) req.campaign_id = campaignId;

  // Coordinates
  if (coords) {
    req.latitude = coords.latitude;
    req.longitude = coords.longitude;
  }

  // User info
  if (userInfo.email) req.email = userInfo.email;
  if (userInfo.mobile_phone) req.mobile_phone = userInfo.mobile_phone;

  if (!userInfo.email && !userInfo.mobile_phone) {
    if (user) {
      if (user.email) req.email = user.email;
      if (user.mobile_phone) req.mobile_phone = user.mobile_phone;
    }
  }

  try {
    const { data } = await axios.post(`/microsite/user-lookup/`, req);
    const user = data.response[0];

    if (user) {
      // Handle modal/lock data
      if (user.modal && onModal) {
        return onModal(user);
      }

      // Set user data in store
      setUser(user);
      setEmail(user.email || userInfo.email);
      setPreferredLocation(user.preferred_location || null);

      // Set email streams if available
      if (user.email_streams && user.email_streams.length) {
        setEmailStreams(
          user.email_streams.map((m) => ({
            ...m,
            value: m.active,
            label: m.name,
          }))
        );
      }

      // Set phone
      if (userInfo.mobile_phone) {
        setPhone(userInfo.mobile_phone);
      } else if (user.mobile_phone) {
        setPhone(user.mobile_phone);
      }

      if (user.non_recurring_data) {
        setRegistrationInfo(user.non_recurring_data);
      }

      return user;
    } else {
      // No user found, set basic info
      if (userInfo.email) setEmail(userInfo.email);
      if (userInfo.mobile_phone) setPhone(userInfo.mobile_phone);
      return null;
    }
  } catch (error) {
    toast.error(error.message || 'User lookup failed');
    throw error;
  }
}
